import { useState, useEffect } from 'react';
import { Box, Card, CardContent, Divider, Tabs, Tab, Button } from '@mui/material';
import HttpServices from 'src/services/httpService';
import { integrateModuleURL, postsModuleURL } from 'src/services/urlService';
import Spinner from 'src/utils/Loader/spinner';
import { useToaster } from 'src/utils/toaster/toasterContext';
import SemiAutomaticTabsWrapper from './semi-automatic-tabs-wrapper.component';
import AiCommentMessageComponent from './ai-message-comments.component';
import FullyAutomaticTab from './fully-automatic.component';
import AIAutoPilotV2 from './ai-auto-pilot-v2.component';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PostAddIcon from '@mui/icons-material/PostAdd';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import TuneIcon from '@mui/icons-material/Tune';
import { useTranslation } from 'react-i18next';

const AutoPostScheduler = () => {
  const { t: translate } = useTranslation();
  const [isAutoPostSetupComplete, setIsAutoPostSetupComplete] = useState(false);
  const [autoPostBrandDetails, setAutoPostBrandDetails] = useState(null);
  const [activeTab, setActiveTab] = useState(0); // Default to the second tab
  const [isLoading, setIsLoading] = useState(true);
  const [imageModels, setImageModels] = useState([]);
  const [selectedImageModel, setSelectedImageModel] = useState(null);
  const { showToast } = useToaster();

  useEffect(() => {
    fetchAutoPostBrandDetails();
  }, []);

  useEffect(() => {
    // Fetch image models on mount
    HttpServices.Get({
      url: integrateModuleURL.getAllImageModels,
      successCallback: (data) => {
        setImageModels(data || []);
        if (data && data.length > 0) setSelectedImageModel(data[0]);
      },
      failureCallback: () => setImageModels([]),
    });
  }, []);

  const fetchAutoPostBrandDetails = () => {
    setIsLoading(true);
    HttpServices.Get({
      url: postsModuleURL.getAutoPostDetails,
      successCallback: (data) => {
        setAutoPostBrandDetails(data);
        setIsAutoPostSetupComplete(data.is_autopost_setup_complete);
        setIsLoading(false);
      },
      failureCallback: () => setIsLoading(false),
    });
  };

  const handleTabChange = (_, newValue) => {
    setActiveTab(newValue);
    fetchAutoPostBrandDetails();
  };

  return (
    <Box sx={{ width: '100%', height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Card
        elevation={6}
        sx={{ flex: 1, borderRadius: 0, display: 'flex', flexDirection: 'column', height: 'calc(100vh - 32px)', mb: 4 }}
      >
        <CardContent sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', pb: 1.5 }}>
            <Tabs value={activeTab} onChange={handleTabChange} sx={{ flexGrow: 1 }}>
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <AutoAwesomeIcon sx={{ mr: 1 }} />
                    {translate('auto_post.auto_post', 'Auto Post')}
                  </Box>
                }
              />
              {/* <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <TuneIcon sx={{ mr: 1 }} />
                    {translate('auto_post.ai_auto_pilot_v2', 'AI Auto Pilot v2 (Beta)')}
                  </Box>
                }
              /> */}
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <PostAddIcon sx={{ mr: 1 }} />
                    {translate('auto_post.semi_automatic_post', 'Semi Automatic Post')}
                  </Box>
                }
              />
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <ChatBubbleOutlineIcon sx={{ mr: 1 }} />
                    {translate('auto_post.ai_messages_comments', 'AI Messages/ Comments')}
                  </Box>
                }
              />
            </Tabs>
            {autoPostBrandDetails?.connected_profiles_count === 0 && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexGrow: 1,
                  backgroundColor: '#fff4e5',
                  border: '1px solid #ffa726',
                  borderRadius: '8px',
                  padding: '12px',
                  marginRight: 2,
                  color: '#ff6f00',
                  textAlign: 'center',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                }}
              >
                {translate('auto_post.no_profiles_connected', 'No profiles connected. Please')}&nbsp;
                <a href="/integration" style={{ color: '#ff6f00', textDecoration: 'underline', fontWeight: 'bold' }}>
                  {translate('auto_post.connect_here', 'connect here')}
                </a>
                .
              </Box>
            )}
            {autoPostBrandDetails && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, pr: 2 }}>
                <Box>
                  <a
                    href="/manage-brand"
                    style={{ color: '#1976d2', textDecoration: 'underline', fontSize: '0.875rem' }}
                  >
                    {translate('auto_post.add_custom_key', 'Add your custom key')}
                  </a>
                </Box>
                <Box>{translate('auto_post.content_tokens', 'Content Tokens')}: {autoPostBrandDetails.ai_content_tokens / 100}</Box>
                <Box>{translate('auto_post.image_tokens', 'Image Tokens')}: {autoPostBrandDetails.ai_image_tokens}</Box>
              </Box>
            )}
          </Box>

          <Divider sx={{ mx: 3 }} />

          <Box sx={{ flex: 1, overflow: 'auto', p: 1 }}>
            {isLoading ? (
              <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Spinner />
              </Box>
            ) : null}
            {activeTab === 0 &&(
                <FullyAutomaticTab
                  autoPostBrandDetails={autoPostBrandDetails}
                  fetchAutoPostBrandDetails={fetchAutoPostBrandDetails}
                  imageModels={imageModels}
                  selectedImageModel={selectedImageModel}
                  setSelectedImageModel={setSelectedImageModel}
                />
              )}{' '}
            {/* {activeTab === 1 && (
              <AIAutoPilotV2
                imageModels={imageModels}
                selectedImageModel={selectedImageModel}
              />
            )} */}
            {activeTab === 1 && (
              <SemiAutomaticTabsWrapper
                imageModels={imageModels}
                selectedImageModel={selectedImageModel}
                setSelectedImageModel={setSelectedImageModel}
              />
            )}
            {activeTab === 2 && (
              <AiCommentMessageComponent
              />
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AutoPostScheduler;
